import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ConversationPrompts, SearchQuery } from 'utils/gloo-types';
import { queryConversationPrompts } from 'actions/requests';
import { useThunkDispatch } from 'utils/reduxHook';
import { useIsMobile } from 'utils/hooks';
import ConversationPromptsFilters from 'components/organisms/ConversationPromptsFilters';
import ConversationPromptsList from 'components/organisms/ConversationPromptsList';
import ConversationPromptDetails from 'components/molecules/ConversationPromptDetails';
import ConversationPromptCreateEdit from 'components/molecules/ConversationPromptCreateEdit';
import Button from 'components/atoms/Button';

const AdminConversationPrompts = () => {
  const dispatch = useThunkDispatch();
  const [prompts, setPrompts] = useState<ConversationPrompts[]>([]);
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    entity: 'conversationPrompts',
    searchString: '',
    searchFields: ['name', 'message', 'author'],
    orderBy: 'updatedAt',
    orderDirection: 'ASC',
    filters: [],
    pagination: { page: 1, limit: 20 },
  });

  const [selectedPrompt, setSelectedPrompt] = useState<ConversationPrompts>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const isMobile = useIsMobile();
  const isInMobileDetailsView = selectedPrompt && isMobile;
  const history = useHistory();

  const pathname = history.location.pathname;
  const [pageState, setPageState] = useState(
    pathname.includes('create') ? 'create' : pathname.includes('edit') ? 'edit' : 'list',
  );

  const loadConversationPrompts = async () => {
    try {
      setSelectedPrompt(null);
      const { conversationPrompts, count } = await dispatch(
        queryConversationPrompts(prompts, { ...searchQuery, showArchived }),
      );
      if (searchQuery.pagination.page > 1) {
        setPrompts([...prompts, ...conversationPrompts]);
      } else {
        setPrompts(conversationPrompts);
      }
      setHasMore(count > searchQuery.pagination.page * searchQuery.pagination.limit);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    setSearchQuery({
      ...searchQuery,
      pagination: { ...searchQuery.pagination, page: searchQuery.pagination.page + 1 },
    });
  };

  const updatePromptInList = (prompt: ConversationPrompts) => {
    if (prompt.status === 'Archived') {
      const updatedList = prompts.filter((p) => p.id !== prompt.id);
      setPrompts(updatedList);
      setSelectedPrompt(null);
      return;
    }
    const updatedList = prompts.map((p) => {
      return p.id === prompt.id ? prompt : p;
    });

    setPrompts(updatedList);
    setSelectedPrompt(prompt);
  };

  useEffect(() => {
    if (searchQuery.pagination.page === 1) setIsLoading(true);
    loadConversationPrompts();
  }, [searchQuery, history.location, showArchived]);

  useEffect(() => {
    history.listen((loc) => {
      if (loc.pathname.includes('edit')) {
        setPageState('edit');
      } else if (loc.pathname.includes('create')) {
        setPageState('create');
      } else {
        setPageState('list');
      }
    });
  }, [history]);

  return (
    <div className="flex flex-col">
      {pageState === 'list' && !isInMobileDetailsView && (
        <div className="flex justify-between items-center flex-wrap border-b border-glooOffWhite-50 w-full px-6 py-4">
          <ConversationPromptsFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
          />
          <Button
            type="primary"
            onClick={() => history.replace(`${pathname}/create`)}
            data-dd-privacy="allow"
            icon="plus"
            text="New Prompt"
            dataDurableId="createConversationPromptLink"
          />
        </div>
      )}

      <div className="flex">
        {pageState === 'edit' || pageState === 'create' ? (
          <ConversationPromptCreateEdit />
        ) : (
          <>
            {!isInMobileDetailsView && (
              <div className="flex-3">
                <ConversationPromptsList
                  label={isLoading ? 'Loading Prompts' : prompts.length ? 'All Prompts' : 'No Results'}
                  isLoading={isLoading}
                  prompts={prompts}
                  selectedPrompt={selectedPrompt}
                  setSelectedPrompt={setSelectedPrompt}
                  handleLoadMore={handleLoadMore}
                  hasMore={hasMore}
                />
              </div>
            )}
            {selectedPrompt && (
              <ConversationPromptDetails
                prompt={selectedPrompt}
                updatePromptInList={updatePromptInList}
                isInMobileDetailsView={isInMobileDetailsView}
                setSelectedPrompt={setSelectedPrompt}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminConversationPrompts;
